import { analytics } from "@/analytics/analytics";

const API_BASE_URL: string = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

/**
 * Request a password reset email
 */
export async function requestPasswordReset(email: string): Promise<void> {
  console.log("[auth] requestPasswordReset called with email:", email);
  console.log("[auth] API_BASE_URL:", API_BASE_URL);

  const resp = await fetch(`${API_BASE_URL}/auth/request-password-reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  console.log("[auth] Response status:", resp.status, resp.statusText);

  const data = await resp.json();
  console.log("[auth] Response data:", data);

  if (!resp.ok) {
    console.error("[auth] Request failed:", data);
    throw new Error(data?.error || "Failed to request password reset");
  }

  console.log("[auth] Password reset request successful");
}

/**
 * Validate a password reset token
 */
export async function validateResetToken(token: string): Promise<{ email: string }> {
  const resp = await fetch(`${API_BASE_URL}/auth/validate-reset-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.error || "Invalid or expired token");
  }

  return { email: data.email };
}

/**
 * Reset password with a valid token
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const resp = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.error || "Failed to reset password");
  }
}

/**
 * Validate an invitation token
 */
export async function validateInvitationToken(
  token: string
): Promise<{ email: string; customerName: string; alreadyAccepted?: boolean }> {
  const resp = await fetch(
    `${API_BASE_URL}/auth/validate-invitation?token=${encodeURIComponent(token)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.error || "Invalid or expired invitation");
  }

  return {
    email: data.email,
    customerName: data.customerName,
    alreadyAccepted: data.alreadyAccepted,
  };
}

/**
 * Setup password with an invitation token
 */
export async function setupPassword(
  token: string,
  newPassword: string,
  tosAccepted: boolean
): Promise<void> {
  const resp = await fetch(`${API_BASE_URL}/auth/setup-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword, tosAccepted }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.error || "Failed to setup password");
  }
}

/**
 * Accept an invitation via magic link (auto-login, no password required)
 */
export async function acceptInvite(
  token: string,
  tosAccepted: boolean
): Promise<{
  success: boolean;
  user_token?: string;
  user_id?: string;
  email?: string;
  has_jgs_mapping?: boolean;
  userType?: string;
  isEval?: boolean;
  isFirstLogin?: boolean;
  alreadyAccepted?: boolean;
}> {
  const resp = await fetch(`${API_BASE_URL}/auth/accept-invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token, tosAccepted }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.error || "Failed to accept invitation");
  }

  // Store user data in sessionStorage
  try {
    if (data.user_token) sessionStorage.setItem("recruit_ai_token", data.user_token);
    if (data.user_id) sessionStorage.setItem("recruit_ai_user_id", String(data.user_id));
    if (data.user_id) sessionStorage.setItem("recruit_ai_analytics_user_id", String(data.user_id));
    if (typeof data.has_jgs_mapping !== "undefined")
      sessionStorage.setItem("recruit_ai_has_jgs", String(!!data.has_jgs_mapping));
    // Clean up legacy keys
    sessionStorage.removeItem("recruit_ai_auth_token");
    sessionStorage.removeItem("recruit_ai_enduser_token");
    sessionStorage.removeItem("recruit_ai_enduser_user_id");
    sessionStorage.removeItem("recruit_ai_enduser_has_jgs");
  } catch {}

  if (data.user_id) {
    analytics.identify({
      userId: String(data.user_id),
      email: data.email,
      userType: data.isEval ? "eval" : "end_user",
    });
  }

  // Signal branding provider + layout to refetch with the new session cookie
  try {
    window.dispatchEvent(new Event("recruit_ai_redeem_done"));
  } catch {}

  return data;
}

/**
 * Set an initial password for magic-link users who don't have one yet.
 * Requires an active EndUser session (cookie-based auth).
 */
export async function setInitialPassword(newPassword: string): Promise<void> {
  const resp = await fetch(`${API_BASE_URL}/auth/set-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ newPassword }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.error || "Failed to set password");
  }
}

/**
 * Request a login link for passwordless users (magic link re-login)
 */
export async function sendLoginLink(email: string): Promise<void> {
  const resp = await fetch(`${API_BASE_URL}/auth/send-login-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.error || "Failed to send login link");
  }
}
